import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, AlertTriangle, RefreshCcw } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminRebuildStreaksButton — Round 84k Phase E2 (May 2026), refreshed
 * copy in P108av+3 (Feb 2026).
 *
 * Scans `dividend_growth_cache` and recomputes each ticker's
 * consecutive-year dividend streak from the raw annual dividend
 * data. Detects cuts + reseeds curated Kings / Aristocrats /
 * Achievers from `data/dividend_tier_lists.py` when the raw math
 * would otherwise strip them (e.g. a split-year distortion or
 * missing special dividend). Pure-cache work — no external API calls.
 * Takes ~5 seconds.
 *
 * Companion admin button in the same "Streaks · on-demand" group:
 *   • AdminResyncTiersButton — force-syncs the curated dict into
 *     `stock_cache.dividend_tier` + re-warms the Heatmap. Click that
 *     one whenever you EDIT the curated tier lists; click Rebuild
 *     Streaks whenever new dividend history arrives (after a Massive
 *     daily snapshot).
 *
 * Self-hides when MASSIVE_LIVE_FETCH_ENABLED=false — in that mode the
 * older AdminStreakComboButton remains the primary workflow.
 */
export default function AdminRebuildStreaksButton() {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [firing, setFiring] = useState(false);
  const [massiveOn, setMassiveOn] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [{ data: rebuildStatus }, { data: massiveStatus }] = await Promise.all([
        axios.get(`${API}/admin/streaks/rebuild-status`, { withCredentials: true }),
        axios.get(`${API}/admin/massive/snapshot-status`, { withCredentials: true }),
      ]);
      setState(rebuildStatus);
      setMassiveOn(!!massiveStatus?.massive_enabled);
    } catch {
      /* non-admin → silently hide */
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') return undefined;
    fetchStatus();
    const id = setInterval(fetchStatus, state?.in_progress ? 5000 : 60000);
    return () => clearInterval(id);
  }, [user, fetchStatus, state?.in_progress]);

  if (user?.role !== 'admin') return null;
  // Self-hide when Massive is off — old StreakComboButton handles the
  // workflow in that mode (refresh + recompute, single click).
  if (!massiveOn) return null;

  const inProgress = !!state?.in_progress;
  const lastResult = state?.last_result;
  const lastError = state?.last_error;

  const handleFire = async () => {
    if (inProgress || firing) return;
    setFiring(true);
    try {
      await axios.post(`${API}/admin/streaks/rebuild-now`, {}, { withCredentials: true });
      toast.success('Streak rebuild started', {
        description: 'Phase 2 — pure cache math. Takes ~5 seconds.',
      });
      setState((prev) => ({ ...(prev || {}), in_progress: true }));
    } catch (err) {
      toast.error('Could not start rebuild', {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setFiring(false);
    }
  };

  let badge = null;
  if (inProgress) {
    badge = (
      <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300">
        <Loader2 className="w-3 h-3 animate-spin" />
        running…
      </span>
    );
  } else if (lastError) {
    badge = (
      <span className="inline-flex items-center gap-1 text-[10px] text-rose-700 dark:text-rose-300" title={lastError}>
        <AlertTriangle className="w-3 h-3" />
        last run failed
      </span>
    );
  } else if (lastResult) {
    badge = (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300"
        title={`scanned ${lastResult.growth_cache_scanned} · cuts ${lastResult.cuts_detected} · reseeded ${lastResult.reseeded_from_curated}`}
      >
        <CheckCircle2 className="w-3 h-3" />
        {lastResult.cuts_detected || 0} cuts · {lastResult.reseeded_from_curated || 0} reseed
      </span>
    );
  }

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        onClick={handleFire}
        disabled={inProgress || firing}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
        title="Admin: recompute dividend streak COUNTS from `dividend_growth_cache` (raw annual dividend data). Detects cuts + reseeds curated Kings/Aristocrats/Achievers when raw math is distorted. Pure-cache work, ~5 sec. Run this AFTER a Massive daily snapshot brings in fresh dividend data. — Distinct from the sibling 'Resync Tier Labels' button, which syncs the curated dict INTO `stock_cache.dividend_tier` + re-warms the Heatmap."
        data-testid="admin-streaks-rebuild-fire"
      >
        {inProgress || firing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
        Rebuild Streaks
      </button>
      {badge}
    </div>
  );
}
